import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";

export function MediaLightbox({
    items,
    currentIndex,
    isOpen,
    onClose,
    onNavigate,
}) {
    const [isFullscreen, setIsFullscreen] = useState(false);

    const currentItem = items[currentIndex];

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;
            switch (e.key) {
                case "Escape":
                    onClose();
                    break;
                case "ArrowLeft":
                    if (currentIndex > 0) onNavigate(currentIndex - 1);
                    break;
                case "ArrowRight":
                    if (currentIndex < items.length - 1) onNavigate(currentIndex + 1);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = "unset";
        };
    }, [isOpen, currentIndex, items.length, onClose, onNavigate]);

    const handlePrevious = () => {
        if (currentIndex > 0) onNavigate(currentIndex - 1);
    };

    const handleNext = () => {
        if (currentIndex < items.length - 1) onNavigate(currentIndex + 1);
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-9999 bg-black/97 backdrop-blur-md"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-6 bg-gradient-to-b from-black/90 to-transparent">
                        <div className="text-white">
                            <p className="text-sm text-white/80">
                                {currentIndex + 1} of {items.length}
                            </p>
                            <p className="text-xs text-white/60 mt-1">
                                {currentItem.type === "video" ? "Video" : "Photo"}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsFullscreen(!isFullscreen);
                                }}
                                className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all hover:scale-105"
                            >
                                <Maximize2 className="w-5 h-5" />
                            </button>
                            <button
                                onClick={onClose}
                                className="p-3 rounded-full bg-white/10 hover:bg-orange-500 text-white transition-all hover:scale-105"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                    <div
                        className="absolute inset-0 flex items-center justify-center p-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <motion.div
                            key={currentIndex}
                            className={`relative ${isFullscreen ? "w-full h-full" : "max-w-6xl max-h-[85vh]"
                                }`}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.2 }}
                        >
                            <img
                                src={currentItem.url}
                                alt={currentItem.caption}
                                className="w-full h-full object-contain"
                            />
                        </motion.div>
                    </div>
                    {currentIndex > 0 && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handlePrevious();
                            }}
                            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all hover:scale-110"
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </button>
                    )}

                    {currentIndex < items.length - 1 && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleNext();
                            }}
                            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all hover:scale-110"
                        >
                            <ChevronRight className="w-6 h-6" />
                        </button>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
                        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide max-w-6xl mx-auto">
                            {items.map((item, index) => (
                                <button
                                    key={item.id}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onNavigate(index);
                                    }}
                                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden transition-all ${index === currentIndex
                                        ? "ring-2 ring-white scale-110"
                                        : "opacity-60 hover:opacity-100"
                                        }`}
                                >
                                    <img
                                        src={item.type === "video" ? item.thumbnail : item.url}
                                        alt={item.caption}
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
